import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { html, nothing } from 'lit';
import {
  BillingPositionPriceInput,
  formatBillingPositionPrice,
} from '../../../../billingPositions/client/BillingPositionPriceInput.js';
import { getEffectiveBillingPosition } from '../../../../billingPositions/api/effectiveBillingPosition.js';
import { BillingRhythm, translateBillingRhythm } from '../../../../billingPositions/entities/BillingPosition.js';
import { BookingAPI, ChangeBillingPositionValuesForBookingOptions } from '../../../../bookings/api/BookingAPI.js';
import type { GroupParticipantRow } from '../../api/GroupsDetailsAPI.js';

const isKnownBillingRhythm = (rhythm: unknown): rhythm is BillingRhythm =>
  Object.values(BillingRhythm).includes(rhythm as BillingRhythm);

const formatInvalidBillingRhythm = (rhythm: unknown): string =>
  `Ungültiger Rhythmus${rhythm ? `: ${String(rhythm)}` : ''}`;

/**
 * What one booking pays: the group's Abrechnungsposition, or the booking's own price on top of it.
 * The rhythm is always the group's. Every change is written at once; "Standard der Gruppe" removes
 * the override.
 */
export const MemberInlineBillingPositionEdit = stateful(
  (props: { participantRow: GroupParticipantRow; disabled: boolean }) => {
    const groupBillingPosition = props.participantRow.group.billingPosition;
    const override = props.participantRow.booking.billingPosition;
    const [isEditing, setIsEditing] = useState(false);

    if (!groupBillingPosition) {
      return html` <div class="py-1 text-sm text-base-content/70">Die Gruppe hat keine Abrechnungsposition.</div> `;
    }

    const bp = getEffectiveBillingPosition(props.participantRow.booking, { billingPosition: groupBillingPosition });
    const isCustom = !!override;
    const hasInvalidRhythm = Boolean(bp.rhythm) && !isKnownBillingRhythm(bp.rhythm);
    const priceLabel = formatBillingPositionPrice(bp.priceInCents);
    const rhythmLabel = !bp.rhythm
      ? 'Selbstauswahl'
      : hasInvalidRhythm
        ? formatInvalidBillingRhythm(bp.rhythm)
        : translateBillingRhythm(bp.rhythm);

    const changePrice = (priceInCents: number | null) =>
      BookingAPI.changeBpValues(
        new ChangeBillingPositionValuesForBookingOptions({
          bookingId: props.participantRow.booking._id,
          amountInCents: priceInCents,
        }),
      )();

    const resetToGroupValues = async (event: Event) => {
      event.stopPropagation();
      event.preventDefault();
      if (props.disabled) return;

      await changePrice(groupBillingPosition.priceInCents ?? null);
    };

    const stateClasses = hasInvalidRhythm
      ? 'border-error/50 bg-error/5'
      : isCustom
        ? 'border-primary/35 bg-primary/5'
        : 'border-base-300 bg-base-100';
    const dotClasses = hasInvalidRhythm ? 'bg-error' : isCustom ? 'bg-primary' : 'bg-base-300';

    if (isEditing) {
      return html`
        <div
          class=${['flex w-full min-w-0 flex-col gap-2 rounded-lg border px-3 py-2', props.disabled ? 'opacity-70' : '', stateClasses].join(' ')}
          @click=${(event: Event) => event.stopPropagation()}
        >
          <div class="flex min-w-0 items-center gap-2">
            <span class=${['h-1.5 w-1.5 shrink-0 rounded-full', dotClasses].join(' ')}></span>
            <span class="min-w-0 flex-1 truncate text-sm font-semibold">${bp.name}</span>
            ${
              hasInvalidRhythm
                ? html`<span class="badge badge-error badge-outline badge-sm shrink-0">Rhythmus ungültig</span>`
                : nothing
            }
            ${
              isCustom
                ? html`
                    <button
                      type="button"
                      ?disabled=${props.disabled}
                      class="btn btn-ghost btn-xs btn-square shrink-0 text-primary"
                      title="Standard der Gruppe"
                      @click=${resetToGroupValues}
                    >
                      <span class="icon-[material-symbols--restart-alt-rounded] text-base"></span>
                    </button>
                  `
                : nothing
            }
            <button
              type="button"
              class="btn btn-primary btn-sm shrink-0 gap-1"
              @click=${(event: Event) => {
                event.stopPropagation();
                setIsEditing(false);
              }}
            >
              <span class="icon-[material-symbols--check-rounded] text-base"></span>
              Fertig
            </button>
          </div>
          <div class="flex min-w-0 flex-wrap items-start gap-2">
            <div class="w-32 shrink-0">
              ${BillingPositionPriceInput({
                disabled: props.disabled,
                value: bp.priceInCents ?? null,
                onPicked: value => changePrice(value ?? null),
              })}
            </div>
            <div
              class=${[
                'flex min-h-10 items-center text-sm',
                hasInvalidRhythm
                  ? 'rounded-lg border border-error/40 bg-error/10 px-3 font-medium text-error'
                  : 'text-base-content/80',
              ].join(' ')}
              title="Der Rhythmus ist der der Gruppe und gilt für alle ihre Buchungen."
            >
              ${rhythmLabel}
            </div>
          </div>
        </div>
      `;
    }

    return html`
      <button
        type="button"
        ?disabled=${props.disabled}
        class=${[
          'flex w-full min-w-0 items-center gap-2 rounded-lg border px-2 py-1.5 text-left transition-colors',
          props.disabled ? 'cursor-not-allowed opacity-70' : 'hover:border-base-content/30 hover:bg-base-200',
          stateClasses,
        ].join(' ')}
        @click=${(event: Event) => {
          event.stopPropagation();
          if (props.disabled) return;
          setIsEditing(true);
        }}
      >
        <span class=${['h-1.5 w-1.5 shrink-0 rounded-full', dotClasses].join(' ')}></span>
        <span
          class="grid min-w-0 flex-1 grid-cols-[max-content_max-content_max-content_minmax(4rem,0.6fr)] items-center gap-2"
        >
          <span class="truncate text-sm font-medium">${bp.name}</span>
          <span class="truncate text-sm text-base-content/80">${priceLabel}</span>
          <span
            class=${['truncate text-sm', hasInvalidRhythm ? 'font-semibold text-error' : 'text-base-content/80'].join(' ')}
            >${rhythmLabel}</span
          >
          <span class="truncate text-sm text-base-content/70">
            ${
              // a monthly position is billed in every run, so its marker says nothing
              props.participantRow.billedOn &&
              (bp.rhythm === BillingRhythm.YEARLY || bp.rhythm === BillingRhythm.ONCE_CUSTOM)
                ? `zuletzt abgerechnet ${formatCalendarDay(props.participantRow.billedOn, 'MM/yyyy')}`
                : ''
            }
          </span>
        </span>
        <span class="icon-[material-symbols--edit-rounded] shrink-0 text-base text-base-content/45"></span>
      </button>
    `;
  },
);
